/**
 * TemplatePerformancePage — the click-through target from every template
 * row on the overview: one template's KPIs, daily series, where it is used
 * (flow steps + triggers), recent Brevo events, and an Edit button that opens
 * the existing template editor.
 *
 * Route: …/(marketing|transactional)/templates/:key/performance?range=30d
 *        (older ?period= links are adopted once and rewritten to ?range=)
 * Data:  GET /api/admin/email-analytics/templates/:key/performance?period
 */

import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNavigate, useParams } from "react-router-dom";
import PillarPageShell from "../shared/PillarPageShell";
import { StateBadge } from "../../help/StateBadge";
import MarketingTemplateEditorDialog from "../marketing/MarketingTemplateEditorDialog";
import TransactionalTemplateEditorDialog from "../transactional/TransactionalTemplateEditorDialog";
import {
  getTemplatePerformance,
  type EmailKind,
  type TemplatePerformanceResponse,
} from "../../../services/emailAnalyticsApi";
import TimeRangeFilter from "../../common/TimeRangeFilter";
import useTimeRangeParam from "../../common/useTimeRangeParam";
import { emailAnalyticsPeriod } from "../shared/emailAnalyticsRange";
import {
  ErrorPanel,
  FreshnessBanner,
  KpiStrip,
  ProvenanceChip,
  SectionHeader,
  VolumeChart,
  fmtDelay,
  fmtInt,
  fmtWhen,
} from "./emailOverviewShared";

const EVENT_COLOR: Record<string, "success" | "info" | "warning" | "error" | "default"> = {
  delivered: "success",
  opened: "info",
  clicked: "info",
  hardBounces: "error",
  hard_bounce: "error",
  softBounces: "warning",
  soft_bounce: "warning",
  spam: "error",
  complaint: "error",
  unsubscribed: "warning",
  blocked: "error",
  deferred: "warning",
  requests: "default",
  sent: "default",
};

const TemplatePerformancePage: React.FC<{ kind: EmailKind }> = ({ kind }) => {
  const { key = "" } = useParams<{ key: string }>();
  const navigate = useNavigate();
  // `legacyParams: ['period']` keeps existing ?period= deep links working: the
  // old key is honoured once, then rewritten to the canonical ?range=.
  const [period, setPeriod] = useTimeRangeParam({ type: "30d" }, { legacyParams: ["period"] });
  const [data, setData] = useState<TemplatePerformanceResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const [editOpen, setEditOpen] = useState(false);

  const pillar = kind === "nurture" ? "marketing" : "transactional";
  const accent = kind === "nurture" ? "#B8860B" : "#475569";
  const backPath = `/dashboard/communications/${pillar}/overview`;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    const wire = emailAnalyticsPeriod(period);
    getTemplatePerformance(decodeURIComponent(key), wire.period, wire.dates)
      .then((d) => alive && setData(d))
      .catch((e: any) => alive && setError(e?.message || "Request failed"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [key, period, tick]);

  const t = data?.template;
  const s = data?.summary;

  return (
    <PillarPageShell
      pillar={pillar}
      title={t?.template_name || decodeURIComponent(key)}
      subtitle={t ? `Template performance · ${t.template_key}` : "Template performance"}
      rightActions={
        <Stack direction="row" spacing={1} alignItems="center">
          <TimeRangeFilter value={period} onChange={setPeriod} />
          <Tooltip title="Reload"><IconButton size="small" onClick={() => setTick((n) => n + 1)} aria-label="Reload"><RefreshIcon fontSize="small" /></IconButton></Tooltip>
          {t && (
            <Button size="small" variant="contained" startIcon={<EditOutlinedIcon />} onClick={() => setEditOpen(true)} sx={{ bgcolor: accent, "&:hover": { bgcolor: accent } }}>
              Edit template
            </Button>
          )}
        </Stack>
      }
    >
      <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate(backPath)} sx={{ mb: 1.5, textTransform: "none" }}>
        Back to {kind === "nurture" ? "Marketing" : "Transactional"} Overview
      </Button>

      {error ? (
        <ErrorPanel message={error} onRetry={() => setTick((n) => n + 1)} />
      ) : (
        <>
          <FreshnessBanner provider={data?.provider} />

          {/* Identity strip */}
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              {loading || !t ? (
                <Skeleton variant="rounded" height={72} />
              ) : (
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between">
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: "wrap" }}>
                      {t.status && <StateBadge state={t.status} />}
                      <Chip size="small" variant="outlined" label={t.kind === "nurture" ? "marketing" : "transactional"} />
                      {t.category && <Chip size="small" variant="outlined" label={t.category} />}
                    </Stack>
                    <Typography variant="body2"><strong>Subject:</strong> {t.subject_line || <em>none</em>}</Typography>
                    {t.preview_text && <Typography variant="body2" color="text.secondary"><strong>Preview:</strong> {t.preview_text}</Typography>}
                    {t.excerpt && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1, whiteSpace: "pre-wrap" }}>
                        {t.excerpt.replace(/\s+/g, " ").trim().slice(0, 300)}{t.excerpt.length > 300 ? "…" : ""}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ textAlign: { md: "right" }, whiteSpace: "nowrap" }}>
                    <Typography variant="caption" color="text.secondary" display="block">Last sent {fmtWhen(s?.last_sent_at)}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">Updated {fmtWhen(t.updated_at)}</Typography>
                    {s && s.delivered_stitched > 0 && (
                      <Tooltip title="Per-message results stitched back onto our send log by Brevo message-id (events poller / webhook).">
                        <Typography variant="caption" color="text.secondary" display="block">
                          {fmtInt(s.delivered_stitched)} delivered · {fmtInt(s.opened_stitched)} opened (per-message)
                        </Typography>
                      </Tooltip>
                    )}
                  </Box>
                </Stack>
              )}
            </CardContent>
          </Card>

          <KpiStrip summary={s} provider={data?.provider} loading={loading} />

          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, lg: 8 }}>
              <Card variant="outlined">
                <CardContent>
                  <SectionHeader
                    title="Daily"
                    subtitle={`Sends from our log · delivered / opens / clicks from Brevo (template:${decodeURIComponent(key)} tag)`}
                    right={<ProvenanceChip provider={data?.provider} source="brevo" />}
                  />
                  <VolumeChart series={data?.series} loading={loading} accent={accent} height={280} />
                  {data?.provider?.template_metrics_since && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                      {/* Was printed raw, e.g. "2026-07-04T00:00:00.000Z". */}
                      Per-template Brevo engagement is available from {fmtWhen(data.provider.template_metrics_since)}; earlier sends were not tagged by template.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, lg: 4 }}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <SectionHeader title="Used by" subtitle="Every flow step and trigger that sends this template" />
                  {loading ? (
                    <Skeleton variant="rounded" height={160} />
                  ) : !data?.used_by?.length ? (
                    <Typography variant="body2" color="text.secondary">Not referenced by any flow or trigger.</Typography>
                  ) : (
                    <Stack spacing={1.25}>
                      {data.used_by.map((u) => (
                        <Box key={`${u.kind}-${u.id}-${u.step_order ?? ""}`} sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{u.name}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }} noWrap display="block">
                              {u.kind === "flow"
                                ? `${u.key} · step ${u.step_order} · ${fmtDelay(u.delay_hours)}${u.detail ? ` · ${u.detail}` : ""}`
                                : `trigger ${u.key}${u.detail ? ` · on ${u.detail}` : ""}`}
                            </Typography>
                          </Box>
                          <StateBadge state={u.status === "disabled" ? "paused" : u.status} />
                        </Box>
                      ))}
                    </Stack>
                  )}
                  <Divider sx={{ my: 2 }} />
                  <Button size="small" onClick={() => navigate(kind === "nurture" ? "/dashboard/communications/marketing/funnels" : "/dashboard/communications/transactional/triggers")} sx={{ textTransform: "none" }}>
                    {kind === "nurture" ? "Open funnel manager" : "Open triggers"}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card variant="outlined" sx={{ mt: 3 }}>
            <CardContent>
              <SectionHeader
                title="Recent activity"
                subtitle="Latest 50 Brevo events for this template in the period (per-message)"
                right={<ProvenanceChip provider={data?.provider} source="brevo" />}
              />
              {loading ? (
                <Skeleton variant="rounded" height={160} />
              ) : !data?.recent_events?.length ? (
                <Typography variant="body2" color="text.secondary">
                  No per-message events yet. Events arrive from the Brevo poller (every 15 min) and the webhook; only sends tagged <code>template:{decodeURIComponent(key)}</code> appear here.
                </Typography>
              ) : (
                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>When</TableCell>
                        <TableCell>Event</TableCell>
                        <TableCell>Recipient</TableCell>
                        <TableCell>Subject</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.recent_events.map((e, i) => (
                        <TableRow key={`${e.occurred_at}-${e.email}-${i}`}>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtWhen(e.occurred_at)}</TableCell>
                          <TableCell><Chip size="small" label={e.event} color={EVENT_COLOR[e.event] || "default"} variant="outlined" /></TableCell>
                          <TableCell sx={{ fontFamily: "monospace" }}>{e.email}</TableCell>
                          <TableCell sx={{ maxWidth: 380 }}><Typography variant="body2" noWrap>{e.subject || "—"}</Typography></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {t && (kind === "nurture" ? (
        <MarketingTemplateEditorDialog
          open={editOpen}
          mode="edit"
          templateId={t.id}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); setTick((n) => n + 1); }}
        />
      ) : (
        <TransactionalTemplateEditorDialog
          open={editOpen}
          mode="edit"
          templateId={t.id}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); setTick((n) => n + 1); }}
        />
      ))}
    </PillarPageShell>
  );
};

export default TemplatePerformancePage;
